import React from 'react';
import { TournamentCategoryOverview } from '../../utils/categoryOverviewApi';
import { WeighInProtocolStatus } from '../../utils/weighInProtocolApi';
import './CategoryOverviewGrid.scss';

interface CategoryOverviewGridProps {
  overviews: TournamentCategoryOverview[];
  loading?: boolean;
  selectedAgeCategoryId?: number | null;
  selectedWeightCategoryId?: number | null;
  onSelectCategory: (overview: TournamentCategoryOverview) => void;
  onOpenBracket: (bracketId: number) => void;
  showWeighInStatus?: boolean;
  weighInStatusByCategory?: Map<string, WeighInProtocolStatus>;
  onPrintWeighIn?: (status: WeighInProtocolStatus, event: React.MouseEvent<HTMLButtonElement>) => void;
  printingWeighInKey?: string | null;
  onPrintCompetitionProtocol?: (overview: TournamentCategoryOverview, event: React.MouseEvent<HTMLButtonElement>) => void;
  printingCompetitionKey?: string | null;
}

const formatLabel = (format?: string | null) => {
  if (format === 'RoundRobin') return 'Круговая';
  if (format === 'Mixed') return 'Смешанная';
  if (format === 'Olympic') return 'Олимпийская';
  return 'Не определён';
};

export const CategoryOverviewGrid: React.FC<CategoryOverviewGridProps> = ({
  overviews,
  loading,
  selectedAgeCategoryId,
  selectedWeightCategoryId,
  onSelectCategory,
  onOpenBracket,
  showWeighInStatus,
  weighInStatusByCategory,
  onPrintWeighIn,
  printingWeighInKey,
  onPrintCompetitionProtocol,
  printingCompetitionKey,
}) => {
  if (loading) {
    return <p className="category-overview__loading">Загрузка групп…</p>;
  }

  if (overviews.length === 0) {
    return <p className="category-overview__empty">Нет групп с участниками или утверждёнными сетками.</p>;
  }

  const handleClick = (overview: TournamentCategoryOverview) => {
    if (overview.isApproved && overview.mainBracketId) {
      onOpenBracket(overview.mainBracketId);
      return;
    }
    onSelectCategory(overview);
  };

  return (
    <div className="category-overview">
      <div className="category-overview__grid">
        {overviews.map((overview) => {
          const isSelected =
            selectedAgeCategoryId === overview.ageCategoryInTournamentId &&
            selectedWeightCategoryId === overview.weightCategoryInTournamentId;

          const hasParticipants = overview.totalParticipants > 0;
          const viewLabel = overview.isApproved
            ? overview.format === 'RoundRobin'
              ? 'Открыть таблицу'
              : overview.format === 'Mixed'
                ? overview.groupStageFinished
                  ? 'Открыть плей-офф'
                  : 'Открыть группы'
                : 'Открыть сетку'
            : 'Настроить группу';

          const weighInKey = `${overview.ageCategoryInTournamentId}-${overview.weightCategoryInTournamentId}`;
          const weighInStatus = showWeighInStatus && weighInStatusByCategory
            ? weighInStatusByCategory.get(weighInKey)
            : undefined;
          const isPrintingWeighIn = printingWeighInKey === weighInKey;
          const isPrintingCompetition = printingCompetitionKey === weighInKey;
          const canPrintCompetitionProtocol =
            overview.isApproved
            && !!overview.mainBracketId
            && (overview.format === 'Olympic' || overview.format === 'Mixed');

          return (
            <button
              key={`${overview.ageCategoryInTournamentId}-${overview.weightCategoryInTournamentId}`}
              type="button"
              className={[
                'category-overview__card',
                overview.isApproved ? 'category-overview__card--approved' : 'category-overview__card--pending',
                isSelected ? 'category-overview__card--selected' : '',
                !hasParticipants ? 'category-overview__card--empty' : '',
                overview.isFinished ? 'category-overview__card--finished' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => handleClick(overview)}
            >
              <div className="category-overview__card-header">
                {overview.genderLabel && (
                  <span className="category-overview__gender">{overview.genderLabel}</span>
                )}
                <span className="category-overview__age">{overview.ageCategoryName}</span>
                <span className="category-overview__weight">{overview.weightCategoryLabel}</span>
              </div>

              <div className="category-overview__badges">
                <span className={`category-overview__badge category-overview__badge--${overview.isApproved ? 'approved' : 'pending'}`}>
                  {overview.isApproved ? formatLabel(overview.format) : 'Не утверждена'}
                </span>
                {overview.isFinished && (
                  <span className="category-overview__badge category-overview__badge--finished">Завершена</span>
                )}
                {overview.format === 'Olympic' && overview.leftRepechageBracketId && (
                  <span className="category-overview__badge category-overview__badge--approved">Утешение (Л)</span>
                )}
                {overview.format === 'Olympic' && overview.rightRepechageBracketId && (
                  <span className="category-overview__badge category-overview__badge--approved">Утешение (П)</span>
                )}
                {weighInStatus && (
                  <span
                    className={[
                      'category-overview__badge',
                      weighInStatus.isCreated
                        ? 'category-overview__badge--weighin-ok'
                        : 'category-overview__badge--weighin-missing',
                    ].join(' ')}
                  >
                    {weighInStatus.isCreated
                      ? `Взвешивание: ${weighInStatus.entryCount}`
                      : 'Нет протокола взвешивания'}
                  </span>
                )}
              </div>

              <div className="category-overview__stats">
                <div className="category-overview__stat">
                  <span className="category-overview__stat-value">{overview.totalParticipants}</span>
                  <span className="category-overview__stat-label">всего</span>
                </div>
                {overview.isApproved && overview.format === 'Olympic' && (
                  <>
                    <div className="category-overview__stat category-overview__stat--highlight">
                      <span className="category-overview__stat-value">{overview.remainingParticipants}</span>
                      <span className="category-overview__stat-label">осталось</span>
                    </div>
                    <div className="category-overview__stat">
                      <span className="category-overview__stat-value">{overview.eliminatedParticipants}</span>
                      <span className="category-overview__stat-label">выбыло</span>
                    </div>
                  </>
                )}
                {overview.isApproved && overview.format === 'RoundRobin' && (
                  <div className="category-overview__stat category-overview__stat--highlight">
                    <span className="category-overview__stat-value">{overview.remainingParticipants}</span>
                    <span className="category-overview__stat-label">в группе</span>
                  </div>
                )}
                {overview.isApproved && overview.format === 'Mixed' && (
                  <>
                    <div className="category-overview__stat category-overview__stat--highlight">
                      <span className="category-overview__stat-value">
                        {overview.groupStageFinished ? 4 : overview.remainingParticipants}
                      </span>
                      <span className="category-overview__stat-label">
                        {overview.groupStageFinished ? 'в плей-офф' : 'в группе'}
                      </span>
                    </div>
                    {overview.groupStageFinished && (
                      <div className="category-overview__stat">
                        <span className="category-overview__stat-value">{overview.eliminatedParticipants}</span>
                        <span className="category-overview__stat-label">выбыло</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="category-overview__footer">
                <div className="category-overview__action">{viewLabel} →</div>
                {(canPrintCompetitionProtocol && onPrintCompetitionProtocol) || (weighInStatus?.isCreated && onPrintWeighIn) ? (
                  <div className="category-overview__print-actions">
                    {canPrintCompetitionProtocol && onPrintCompetitionProtocol && (
                      <button
                        type="button"
                        className="category-overview__print-btn"
                        disabled={isPrintingCompetition}
                        onClick={(e) => onPrintCompetitionProtocol(overview, e)}
                        title="Печать протокола хода соревнований"
                      >
                        {isPrintingCompetition ? 'PDF…' : 'PDF хода'}
                      </button>
                    )}
                    {weighInStatus?.isCreated && onPrintWeighIn && (
                      <button
                        type="button"
                        className="category-overview__print-btn"
                        disabled={isPrintingWeighIn}
                        onClick={(e) => onPrintWeighIn(weighInStatus, e)}
                        title="Печать протокола взвешивания"
                      >
                        {isPrintingWeighIn ? 'PDF…' : 'PDF взвеш.'}
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryOverviewGrid;
